import { QueryObserverOptions, useQuery } from "react-query";
import { APIError, APIResponse, API_URL_V3, get } from "../api";
import type {
  Installment,
  QuickLoanApi,
} from "../@types";
import { normalizeRepaymentSchedule, isInstallmentPaid, parseScheduleAmount } from "../helpers/loanSchedule";

const QUICK_LOANS_URL = API_URL_V3 + "/alert/loan/quick-loans";

/**
 * Maps a quick loan from GET /api/v3/alert/loan/quick-loans to the Installment
 * shape expected by Plans and LoanItem (same as legacy installments API).
 */
function mapQuickLoanToInstallment(ql: QuickLoanApi): Installment {
  const schedule = normalizeRepaymentSchedule(ql.repayment_schedule);
  const totalAmount = Number(ql.loan_amount) || 0;
  const numInstallments = schedule.length || 1;
  const paidCount = schedule.filter(isInstallmentPaid).length;
  const firstUnpaid = schedule.find((s) => !isInstallmentPaid(s));
  const perInstallment =
    firstUnpaid?.RemainingTotal != null
      ? String(firstUnpaid.RemainingTotal)
      : (ql.per_installment_amount ?? ql.per_instalment_amount) != null
      ? String(ql.per_installment_amount ?? ql.per_instalment_amount)
      : numInstallments > 0
        ? (totalAmount / numInstallments).toFixed(2)
        : "0";
  const nextPaymentDate = firstUnpaid?.PaymentDueDate ?? "";

  const installmentsList = schedule.map((s) => ({
    amount: parseScheduleAmount(s.Total) || 0,
    processing_fee: "0",
    due: s.PaymentDueDate ?? "",
    paid: isInstallmentPaid(s),
    status: s.status ?? "pending",
    paid_at: null as any,
  }));

  const industry = (ql.industry ?? "").toLowerCase();
  const service =
    industry === "aviation" ? "AVIATION" : industry ? industry.toUpperCase() : "COMMERCE";

  return {
    id: ql.id,
    service,
    total_amount: totalAmount,
    number_of_installments: numInstallments,
    single_installment_amount: parseFloat(perInstallment) || 0,
    current_installment: paidCount,
    next_payment_date: nextPaymentDate,
    loan_status: ql.status ?? "pending",
    installments: installmentsList as unknown as Installment[],
    status: ql.status ?? "pending",
    active: paidCount < numInstallments ? 1 : 0,
    loan: {
      id: ql.id,
      public_id: ql.id,
      user_id: ql.user_id ?? "",
      partner_id: "",
      loan_amount: String(ql.loan_amount ?? 0),
      interest_rate: "",
      tenure_in_months: 0,
      transaction_fee: "",
      loan_payment_status: "",
      payment_target: "",
      start_date: "",
      end_date: "",
      outstanding_amount: "",
      target_service_id: null,
      merchant: "",
      reference: "",
      calculated_interest: "",
      down_payment: "",
      per_installment_amount: perInstallment,
      industry: ql.industry ?? "BNPL",
      currency: "NGN",
      status: ql.status ?? "pending",
      reason: null,
      utilized: 0,
      transaction_cost: "",
      created_at: ql.created_at ?? "",
      updated_at: ql.updated_at ?? "",
      deleted_at: null,
      installment: installmentsList[0] as any,
    },
    created_at: ql.created_at ?? "",
    updated_at: ql.updated_at ?? "",
  } as Installment;
}

function useLoans(
  options?: QueryObserverOptions<APIResponse<Installment[]>, APIError>
) {
  const {
    isLoading: fetchingInstallments,
    isError: isInstallmentsError,
    data: installments,
    refetch: refreshInstallments,
  } = useQuery<APIResponse<Installment[]>, APIError>(
    "quick-loans",
    async () => {
      const res = await get(
        QUICK_LOANS_URL,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const list = res?.data ?? [];
      const mapped = Array.isArray(list) ? list.map(mapQuickLoanToInstallment) : [];
      return {
        status: true,
        data: mapped,
        message: (res as any)?.message,
      } as APIResponse<Installment[]>;
    },
    options
  );

  return {
    fetchingInstallments,
    isInstallmentsError,
    installments,
    refreshInstallments,
  };
}

export default useLoans;
